import type { paths } from "@/api-schema";
import { MergeEntityName } from "@/api-schema-enums";
import { alphartisApiAxios } from "@/config/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BRAND_SEARCH_RESOURCE } from "../brand/use-brand-search.query";
import { MODEL_FAMILY_SEARCH_RESOURCE } from "../model-family/use-model-family-search.query";
import { MODEL_GENERATION_SEARCH_RESOURCE } from "../model-generation/use-model-generation-search.query";
import { VEHICLE_SEARCH_RESOURCE } from "../vehicle/use-vehicle-search.query";

export const MERGE_RESOURCE = "merge" as const;
export const MERGE_EXECUTE_API_PATH = `/v1/${MERGE_RESOURCE}/execute` as const;

export type MergeExecuteInput =
  paths[typeof MERGE_EXECUTE_API_PATH]["post"]["requestBody"]["content"]["application/json"];

const INVALIDATION_KEYS: Record<MergeEntityName, readonly string[]> = {
  [MergeEntityName.Brand]: [
    BRAND_SEARCH_RESOURCE,
    MODEL_FAMILY_SEARCH_RESOURCE,
    MODEL_GENERATION_SEARCH_RESOURCE,
    VEHICLE_SEARCH_RESOURCE,
  ],
  [MergeEntityName.ModelFamily]: [
    MODEL_FAMILY_SEARCH_RESOURCE,
    MODEL_GENERATION_SEARCH_RESOURCE,
    VEHICLE_SEARCH_RESOURCE,
  ],
  [MergeEntityName.ModelGeneration]: [MODEL_GENERATION_SEARCH_RESOURCE, VEHICLE_SEARCH_RESOURCE],
};

export function useMergeMutation() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, MergeExecuteInput>({
    mutationFn: async (body) => {
      const response = await alphartisApiAxios.post(MERGE_EXECUTE_API_PATH, body);
      return response.data;
    },
    onSuccess: async (_data, variables) => {
      const keys = INVALIDATION_KEYS[variables.entity] ?? [];
      await Promise.all(keys.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
    },
  });
}
